import { fmtAmount } from '../format';
import type { OverviewFields } from './types-overview';

// Field definitions for the RC Overview — the SSOT the section table and the edit
// panel both read, so they can't drift (mirror of DO's overview-utils). `key` is
// the camelCase response key; `requestKey` is the snake_case key the PATCH body
// uses (matches the BE FIELD_MAPPING). Derived fields are editable: false — shown
// read-only in the table, excluded from the panel. `display` formats the cell.
export interface OverviewFieldDef {
  key: keyof OverviewFields;
  label: string;
  requestKey: string;
  input: 'text' | 'date' | 'boolean' | 'number';
  editable: boolean;
  display?: 'amount' | 'boolean';
}

export const OVERVIEW_FIELDS: OverviewFieldDef[] = [
  {
    key: 'name',
    label: 'Royalty Check Name',
    requestKey: 'name',
    input: 'text',
    editable: false,
  },
  {
    key: 'sourceFormat',
    label: 'Source Format',
    requestKey: 'source_format',
    input: 'text',
    editable: false,
  },
  {
    key: 'checkIssueDate',
    label: 'Check Issue Date',
    requestKey: 'check_issue_date',
    input: 'date',
    editable: true,
  },
  {
    key: 'operatorName',
    label: 'Operator Name',
    requestKey: 'operator_name',
    input: 'text',
    editable: true,
  },
  {
    key: 'ownerName',
    label: 'Owner Name',
    requestKey: 'owner_name',
    input: 'text',
    editable: true,
  },
  {
    key: 'checkAmount',
    label: 'Check Amount',
    requestKey: 'check_amount',
    input: 'number',
    editable: true,
    display: 'amount',
  },
  {
    key: 'checkAmountCalculated',
    label: 'Check Amount Calculated',
    requestKey: 'check_amount_calculated',
    input: 'text',
    editable: false,
    display: 'amount',
  },
  {
    key: 'lineItemVariance',
    label: 'Line Item Variance',
    requestKey: 'line_item_variance',
    input: 'text',
    editable: false,
    display: 'amount',
  },
  {
    key: 'depositedByAccounting',
    label: 'Deposited by Accounting',
    requestKey: 'deposited_by_accounting',
    input: 'boolean',
    editable: true,
    display: 'boolean',
  },
  {
    key: 'depositDate',
    label: 'Deposit Date',
    requestKey: 'deposit_date',
    input: 'date',
    editable: true,
  },
  {
    key: 'excludeAllRevenue',
    label: 'Exclude All Revenue',
    requestKey: 'exclude_all_revenue',
    input: 'boolean',
    editable: true,
    display: 'boolean',
  },
];

export const EDITABLE_OVERVIEW_FIELDS = OVERVIEW_FIELDS.filter(
  (f) => f.editable
);

const EM_DASH = '—';

export const fieldDisplayValue = (
  def: OverviewFieldDef,
  fields: OverviewFields
): string => {
  const raw = fields[def.key];
  if (raw === null || raw === undefined || raw === '') return EM_DASH;
  if (def.display === 'boolean') return raw ? 'Yes' : 'No';
  if (def.display === 'amount') return fmtAmount(raw as number);
  return String(raw);
};

// Panel seed value: '' for blank, 'true'/'false' for booleans, string otherwise.
export const fieldInputValue = (
  def: OverviewFieldDef,
  fields: OverviewFields
): string => {
  const raw = fields[def.key];
  if (raw === null || raw === undefined) return '';
  if (def.input === 'boolean') return raw ? 'true' : 'false';
  return String(raw);
};
